import React, { type ChangeEvent, useCallback, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import { useForm } from '@tanstack/react-form';
import { useSelector } from '@tanstack/react-store';
import { z } from 'zod';

import { useAnalyticsPrefix, useAuth } from '../../AuthContext';
import { DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE } from '../../ctx/initAuthContext';
import type { AuthError, AuthErrorCode } from '../../sessionClient';
import { RESET_PASSWORD_STEP, useAccountForm } from '../AccountFormContext';
import { AuthFormError } from './primitives/AuthFormError';
import { OrDivider } from './primitives/OrDivider';
import { PasswordInput } from './primitives/PasswordInput';

/**
 * Steps this module routes to. The destination screens are owned by sibling
 * module tickets (magic-link: GROWTH-2592; reset-password: GROWTH-2594); this
 * step only advances the shared step so the shell can swap the screen.
 */
const MAGIC_LINK_STEP = 'magic-link';

const ANALYTICS_COMPONENT = 'login';

/**
 * Non-personalized fallback heading. `GET /api/v1/humans/status` returns only
 * `has_generated_password` today (no name), so the Figma's "Welcome back,
 * {firstName}!" degrades to this until the backend carries a name; a consumer
 * that has the name can pass it through the `heading` prop.
 */
const DEFAULT_HEADING = 'Welcome back!';

/**
 * User-facing copy per typed error `code`, taken from the Figma error frames —
 * never a substring match on the raw Rails string (that classification already
 * happened in `sessionClient`), so a copy tweak on the backend can't silently
 * drop us to the generic message.
 */
const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  'invalid-credentials': 'Invalid email or password',
  'lockout-imminent':
    'Incorrect password. You have 1 attempt left before your account is locked for 1 hour.',
  locked:
    'Too many failed attempts. Your account is locked for 1 hour — reset your password to unlock it now.',
  'validation-failed': DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  'network-error': DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  'server-error': DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  unknown: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
};

const passwordSchema = z.object({ password: z.string().min(1, 'Enter your password') });

/**
 * Failures that describe the account rather than the attempt, so a submit that
 * never reached the server does not make them stale. `lockout-imminent` warns
 * about the *next* try, so it has to survive the retyping it prompts.
 *
 * `locked` belongs to the same class — it is the state the reset call-to-action
 * hangs off — but cannot reach this path today, because a `locked` failure
 * collapses the field and Confirm entirely. It is listed so the exemption is the
 * class rather than one case, should that collapse ever go away.
 */
const SURVIVES_BLOCKED_SUBMIT: ReadonlySet<AuthErrorCode> = new Set(['lockout-imminent', 'locked']);

export interface PasswordModuleProps {
  /**
   * Heading copy. Omitted renders the Figma default ("Welcome back!"); `null`
   * renders no heading (for a shell that supplies its own); any node renders as
   * given. A thin naming slot, per the `forms.ts` boundary — not a general copy
   * prop bag.
   */
  heading?: React.ReactNode;
  /**
   * Subheading copy. Omitted renders "Signing in as {email}" from the email
   * captured upstream (and nothing when no email was captured); `null` renders
   * nothing; any node renders as given.
   */
  subheading?: React.ReactNode;
}

/**
 * The returning-user password step: a "welcome back" screen that signs the
 * captured email in with a password, surfaces typed auth failures in the shared
 * `AuthFormError` region, and offers the reset and magic-link alternatives. On
 * a `locked` failure the field collapses to a reset call-to-action, matching
 * the Figma. Usable standalone under a bare `AccountFormProvider` or composed
 * by the shell when the email lookup routes to the `password` step.
 */
export const PasswordModule: React.FC<PasswordModuleProps> = ({
  heading = DEFAULT_HEADING,
  subheading,
}) => {
  const { email, setStep, onSuccess, onError } = useAccountForm();
  const { logIn } = useAuth();
  const analyticsPrefix = useAnalyticsPrefix();
  const [authError, setAuthError] = useState<AuthError | null>(null);

  const analyticsName = (action: string): string =>
    `${analyticsPrefix}-${ANALYTICS_COMPONENT}-${action}`;

  const goToMagicLink = useCallback(() => setStep(MAGIC_LINK_STEP), [setStep]);
  const goToResetPassword = useCallback(() => setStep(RESET_PASSWORD_STEP), [setStep]);

  const form = useForm({
    defaultValues: { password: '' },
    validators: { onChange: passwordSchema },
    // A Confirm press that fails client validation skips onSubmit, so clear a
    // prior server failure here too — otherwise its message lingers in the
    // alert region alongside the new "Enter your password" field error. The
    // lockout codes are exempt: they are about the account, not the attempt.
    onSubmitInvalid: () =>
      setAuthError((prev) =>
        prev != null && SURVIVES_BLOCKED_SUBMIT.has(prev.code) ? prev : null,
      ),
    onSubmit: async ({ value }) => {
      // Clear any prior failure so a retry starts from a clean error region.
      setAuthError(null);
      // useAuth().logIn resolves an AuthResult (never throws), so a failure
      // routes into the error region below rather than escaping onSubmit as an
      // unhandled promise rejection.
      const result = await logIn({ email, password: value.password });
      if (!result.success) {
        setAuthError(result.error);
        // Notify the shell's error hook too: the alert region is the primary
        // UX, `onError` is the observability/toast side channel a shell may
        // wire (matching EmailModule's contract).
        onError?.(result.error);
        return;
      }
      onSuccess?.({ email, redirectTo: result.data.redirectTo });
    },
  });

  // `logIn`'s verdict describes the password that was submitted, so the value it
  // describes must not move while the request is in flight — otherwise
  // "Invalid email or password" can land above a password the user has since
  // corrected, and be read as a verdict on the correction.
  //
  // `readOnly`, not `disabled`. A disabled control is not focusable, so a browser
  // blurs it the moment it disables — pressing Enter to submit would drop focus,
  // and the retry the failure message asks for would start by hunting for the
  // field again. `readOnly` freezes the value while keeping the field focusable
  // and in the tab order, and password managers skip it the same way.
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting);

  const locked = authError?.code === 'locked';
  const serverErrorMessage = authError ? AUTH_ERROR_MESSAGES[authError.code] : null;
  const resolvedSubheading =
    subheading === undefined ? (email ? `Signing in as ${email}` : null) : subheading;

  return (
    <div className="flex w-full flex-col gap-4">
      {heading != null ? (
        <Text as="h1" className="text-subheader-md text-od-content-primary">
          {heading}
        </Text>
      ) : null}
      {resolvedSubheading != null ? (
        <Text as="p" className="text-body-sm text-od-content-secondary">
          {resolvedSubheading}
        </Text>
      ) : null}
      {locked ? (
        <>
          {/* No form in the locked branch, so AuthFormError renders the server
              message on its own. */}
          <AuthFormError serverErrors={serverErrorMessage} />
          <Button
            className="w-full"
            variant="primary"
            aria-label="Reset password"
            analyticsName={analyticsName('reset-password')}
            onClick={goToResetPassword}
          >
            Reset password
          </Button>
        </>
      ) : (
        <Form.Root
          form={form}
          noValidate
          scrollToFirstError={false}
          className="flex w-full flex-col gap-4"
        >
          {/* Inside Form.Root so the single alert region carries both the
              server failure and the client field-validation message — no
              inline field errors, no split error UX. */}
          <AuthFormError serverErrors={serverErrorMessage} />
          <div className="flex w-full flex-col gap-2">
            <Text as="p" className="text-label-md text-od-content-primary">
              Password
            </Text>
            <form.Field name="password">
              {(field) => (
                <PasswordInput
                  aria-label="Password"
                  name={field.name}
                  autoComplete="current-password"
                  analyticsName={analyticsName('password')}
                  value={typeof field.state.value === 'string' ? field.state.value : ''}
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- must map the change event to the field value inside the Field render prop, where no hook can hoist it
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  readOnly={isSubmitting}
                  error={authError != null}
                />
              )}
            </form.Field>
          </div>
          <Box
            onClick={goToResetPassword}
            aria-label="Forgot your password?"
            analyticsName={analyticsName('forgot-password')}
            className="self-start text-label-md text-od-content-accent-brand hover:underline"
          >
            Forgot your password?
          </Box>
          <Form.SubmitButton
            className="w-full"
            aria-label="Confirm"
            analyticsName={analyticsName('confirm')}
          >
            Confirm
          </Form.SubmitButton>
        </Form.Root>
      )}
      <OrDivider />
      <Text as="p" className="text-label-md text-od-content-primary">
        Get a link to login sent to your email
      </Text>
      <Button
        className="w-full"
        variant="secondary"
        aria-label="Email a login link"
        analyticsName={analyticsName('magic-link')}
        onClick={goToMagicLink}
      >
        Email a login link
      </Button>
    </div>
  );
};
